// [HOOK SIMPLE] Détection première connexion pour auto-start tutoriels
// [RÔLE] Vérifier si c'est la première connexion et déclencher tutoriels automatiquement
// [ARCHITECTURE] Hook React simple avec localStorage + cookies + API response

import { useState, useEffect } from 'react';
import { removeAuthCookie } from '../utils/authUtils';

interface FirstLoginState {
  isFirstLogin: boolean;
  isLoading: boolean;
  hasChecked: boolean;
}

export const useFirstLogin = () => {
  const [state, setState] = useState<FirstLoginState>({
    isFirstLogin: false,
    isLoading: true,
    hasChecked: false
  });

  useEffect(() => {
    const checkFirstLogin = () => {
      try {
        // [VÉRIFICATION PRÉALABLE] Vérifier si le tutoriel a déjà été déclenché
        const alreadyTriggered = localStorage.getItem('famla_first_login_done');
        
        // [PRIORITÉ 1] Vérifier flag API de session (login classique) - OBLIGATOIRE
        // Ce flag est défini par handleLoginSuccess lors du login
        const apiFirstLogin = sessionStorage.getItem('api_first_login');
        if (apiFirstLogin === 'true' && alreadyTriggered !== 'true') {
          console.log('🎯 Première connexion détectée via API response (is_first_login=true)');
          setState({
            isFirstLogin: true,
            isLoading: false,
            hasChecked: true
          });
          
          return;
        }

        // [PRIORITÉ 2] Vérifier cookie temporaire (Google SSO / ST SSO) - OBLIGATOIRE
        const cookieFirstLogin = document.cookie
          .split('; ')
          .find(row => row.startsWith('is_first_login='))
          ?.split('=')[1];

        if (cookieFirstLogin === 'true' && alreadyTriggered !== 'true') {
          console.log('🎯 Première connexion détectée via cookie SSO (is_first_login=true)');
          setState({
            isFirstLogin: true,
            isLoading: false,
            hasChecked: true
          });
          
          return;
        }

        // [AUCUN FLAG DÉTECTÉ] Connexion normale - PAS de première connexion
        // Le tutoriel ne se déclenche QUE si is_first_login=true vient de l'API/cookie
        console.log('✅ Connexion normale - is_first_login non détecté (pas de tutoriel auto)');
        setState({
          isFirstLogin: false,
          isLoading: false,
          hasChecked: true
        });

      } catch (error) {
        console.warn('Erreur détection première connexion:', error);
        setState({
          isFirstLogin: false,
          isLoading: false,
          hasChecked: true
        });
      }
    };

    // [DÉLAI] Attendre que la page soit chargée
    const timer = setTimeout(checkFirstLogin, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // [FONCTION] Forcer reset pour tests
  const resetFirstLogin = () => {
    localStorage.removeItem('famla_first_login_done');
    localStorage.removeItem('tutorial_home_auto_started');
    localStorage.removeItem('tutorial_processCreate_auto_started');
    removeAuthCookie('is_first_login');
    sessionStorage.removeItem('api_first_login');
    setState({
      isFirstLogin: false,
      isLoading: false,
      hasChecked: false
    });
  };

  return {
    isFirstLogin: state.isFirstLogin,
    isLoading: state.isLoading,
    hasChecked: state.hasChecked,
    resetFirstLogin // Pour debug/tests
  };
};

export default useFirstLogin;
